/**
 * What may be done to a selection of tasks — `KLQ-20260825-006` §H.
 *
 * **One constructor, two doors.** The list built here is drawn by the context menu on
 * right-click and by the selection action bar at the bottom of the screen (§G). Neither
 * renderer knows what a task is and this file knows nothing about popups; an action added
 * here appears in both, which is the only arrangement in which they cannot drift.
 *
 * **Every item states its own applicability** (§C.2). `hidden` sits beside the label rather
 * than in a filter further up, so *"why is Close not offered on this selection?"* is answered
 * by reading one object. And the rules are all of the same shape — *is there a subject task
 * this could actually be done to* — because §D.4 is the governing rule: a mixed selection is
 * filtered, never refused.
 *
 * **The transitions are the server's answer, not a table copied from it.** Every task carries
 * `allowed_transitions`, computed per caller, and *Mark done* is offered exactly when some
 * subject task says it may. A second status graph in the client is a second thing to be wrong
 * about, and `TaskBoard` already made this call for the same reason.
 *
 * TWO DELIBERATE DIVERGENCES FROM §H.1, BOTH NARROWER RATHER THAN WIDER
 *
 * 1. **Assign and Unassign are one level of names, not a picker.** §H.2's rule survives whole
 *    — the wire is a delta, so *"Peter left"* is *Remove Peter* then *Add Zuzana*, two acts
 *    that read as two acts — but the names are a submenu instead of a dialog, because a
 *    project has a dozen members and a dialog to pick one of twelve is a dialog too many.
 * 2. **Due date is *Clear* only.** Setting one needs a calendar, and this product already has
 *    a date editor that fans out over exactly this selection: the `when` cell
 *    (`KLQ-20260828-001`). A second date picker inside a menu would be a second way to write
 *    the same field, disagreeing about parsing the moment either changes. Clearing needs no
 *    control at all, so it is here.
 */

import {
  CalendarX,
  Check,
  CircleDot,
  ExternalLink,
  Flag,
  Play,
  Tag,
  TagsIcon,
  UserMinus,
  UserPlus,
} from "lucide-react";

import type { MenuItem } from "@/components/MenuPopup";
import type { Translate } from "@/lib/i18n";

import type { ProjectLabel, Task, TaskBulkEdit, TaskPriority, TaskStatus } from "./api";

/** One act, ready to be confirmed and written. The ids are added by the surface. */
export interface TaskBulkIntent {
  /** The bulk-edit body without `task_ids` — the surface owns the subject, this owns the act. */
  readonly patch: Omit<TaskBulkEdit, "task_ids">;
  /** The act, as words: *"Assign to Zuzana"*. Becomes the question when more than one task
      is about to change (§H.3), and the heading of the result when it is done. */
  readonly what: string;
}

export interface TaskActionsInput {
  /** The subject: the tasks the selection means, already resolved by `planSubjects`. */
  readonly tasks: readonly Task[];
  readonly members: readonly { readonly id: string; readonly label: string }[];
  readonly labels: readonly ProjectLabel[];
  readonly t: Translate<"tasks">;
  /** Ask, then write. The surface owns both beats because it owns the count. */
  readonly onIntent: (intent: TaskBulkIntent) => void;
  /** Client-side navigation, offered only when the subject is exactly one task. */
  readonly onOpen: (taskId: string) => void;
}

const PRIORITIES: readonly TaskPriority[] = ["high", "normal", "low"];

/** The three transitions worth a menu item, with the words the task detail already uses. */
const TRANSITIONS: readonly {
  readonly to: TaskStatus;
  readonly key: "workflow.toInProgress" | "workflow.toDone" | "workflow.toClosed";
}[] = [
  { to: "in_progress", key: "workflow.toInProgress" },
  { to: "done", key: "workflow.toDone" },
  { to: "closed", key: "workflow.toClosed" },
];

/**
 * A body with `clear_due_at` spelled out on every branch.
 *
 * The generated client makes it required — it is a flag with a default on the server, and a
 * default is not an absence — so this is the one place that says `false` and every caller
 * below stays about the act rather than about the schema.
 */
function patchOf(patch: Omit<TaskBulkEdit, "task_ids" | "clear_due_at">): Omit<
  TaskBulkEdit,
  "task_ids"
> {
  return { clear_due_at: false, ...patch };
}

export function taskMenuItems({
  tasks,
  members,
  labels,
  t,
  onIntent,
  onOpen,
}: TaskActionsInput): MenuItem[] {
  // A closed task refuses every edit, so it is not a subject for one — the server says the
  // same thing with `task_closed`, and hiding an item nothing in the selection could take is
  // the difference between a menu and a list of things that will fail.
  const editable = tasks.filter((task) => task.status !== "closed");
  const held = new Set(editable.flatMap((task) => task.assignee_user_ids));
  const heldLabels = new Set(editable.flatMap((task) => task.label_ids));
  const reachable = new Set(tasks.flatMap((task) => task.allowed_transitions));
  const only = tasks.length === 1 ? tasks[0] : undefined;

  const nameOf = (userId: string): string =>
    members.find((one) => one.id === userId)?.label ?? userId;

  const assign: MenuItem = {
    id: "assign",
    label: t("bulk.assign"),
    icon: <UserPlus size={16} strokeWidth={1.5} />,
    hidden: editable.length === 0 || members.length === 0,
    items: members.map((member) => ({
      id: `assign:${member.id}`,
      label: member.label,
      onSelect: () => {
        onIntent({
          patch: patchOf({ assignees: { add: [member.id], remove: [] } }),
          what: t("bulk.assignTo", { name: member.label }),
        });
      },
    })),
  };

  const unassign: MenuItem = {
    id: "unassign",
    label: t("bulk.unassign"),
    icon: <UserMinus size={16} strokeWidth={1.5} />,
    hidden: held.size === 0,
    // Only the people actually on these tasks. Offering the whole studio would make *Remove*
    // a list of no-ops with the one useful name buried in it.
    items: [...held].map((userId) => ({
      id: `unassign:${userId}`,
      label: nameOf(userId),
      onSelect: () => {
        onIntent({
          patch: patchOf({ assignees: { add: [], remove: [userId] } }),
          what: t("bulk.unassignFrom", { name: nameOf(userId) }),
        });
      },
    })),
  };

  const priority: MenuItem = {
    id: "priority",
    label: t("bulk.priority"),
    icon: <Flag size={16} strokeWidth={1.5} />,
    hidden: editable.length === 0,
    items: PRIORITIES.map((value) => ({
      id: `priority:${value}`,
      label: t(`priority.${value}`),
      onSelect: () => {
        onIntent({
          patch: patchOf({ priority: value }),
          what: t("bulk.priorityTo", { value: t(`priority.${value}`) }),
        });
      },
    })),
  };

  const addLabel: MenuItem = {
    id: "label-add",
    label: t("bulk.addLabel"),
    icon: <Tag size={16} strokeWidth={1.5} />,
    hidden: editable.length === 0 || labels.length === 0,
    items: labels.map((label) => ({
      id: `label-add:${label.id}`,
      label: label.name,
      onSelect: () => {
        onIntent({
          patch: patchOf({ labels: { add: [label.id], remove: [] } }),
          what: t("bulk.addLabelTo", { name: label.name }),
        });
      },
    })),
  };

  const removeLabel: MenuItem = {
    id: "label-remove",
    label: t("bulk.removeLabel"),
    icon: <TagsIcon size={16} strokeWidth={1.5} />,
    hidden: heldLabels.size === 0,
    items: labels
      .filter((label) => heldLabels.has(label.id))
      .map((label) => ({
        id: `label-remove:${label.id}`,
        label: label.name,
        onSelect: () => {
          onIntent({
            patch: patchOf({ labels: { add: [], remove: [label.id] } }),
            what: t("bulk.removeLabelFrom", { name: label.name }),
          });
        },
      })),
  };

  const clearDue: MenuItem = {
    id: "due-clear",
    label: t("bulk.clearDue"),
    icon: <CalendarX size={16} strokeWidth={1.5} />,
    hidden: !editable.some((task) => task.due_at != null),
    onSelect: () => {
      onIntent({
        patch: { clear_due_at: true },
        what: t("bulk.clearDue"),
      });
    },
  };

  const transitions: MenuItem[] = TRANSITIONS.map(({ to, key }) => ({
    id: `status:${to}`,
    label: t(key),
    icon:
      to === "in_progress" ? (
        <Play size={16} strokeWidth={1.5} />
      ) : to === "done" ? (
        <Check size={16} strokeWidth={1.5} />
      ) : (
        <CircleDot size={16} strokeWidth={1.5} />
      ),
    // Offered when *some* task can make the move — the rest come back as skips naming their
    // own state, which is a truer answer than an item that was never drawn (§E.3).
    hidden: !reachable.has(to),
    onSelect: () => {
      onIntent({ patch: patchOf({ status: to }), what: t(key) });
    },
  }));

  const open: MenuItem = {
    id: "open",
    label: t("bulk.open"),
    icon: <ExternalLink size={16} strokeWidth={1.5} />,
    hidden: only === undefined,
    onSelect: () => {
      if (only !== undefined) onOpen(only.id);
    },
  };

  return [
    open,
    { id: "sep-open", separator: true },
    assign,
    unassign,
    priority,
    { id: "sep-fields", separator: true },
    addLabel,
    removeLabel,
    clearDue,
    { id: "sep-status", separator: true },
    ...transitions,
  ];
}
